import type { ReactNode } from 'react'
import { useTranslation } from 'next-i18next'
import Pagination from 'shared/components/pagination'
import { useInvitations } from 'modules/community/api/get-invitations'
import { InvitationShort } from 'modules/community/components/member/invitation-short/invitation-short'
import { SkeletonMemberShort } from 'modules/community/components/member/member-short/member-short-skeleton'
import { MembersEmptyList } from 'modules/community/components/member/members-empty-list'
import type { InvitationInterface } from 'modules/community/types/invitation-interface'

interface InvitationsSectionProps {
  communityPath: string
  status: 'expired' | 'pending'
  searchQuery: string
  page: number
  onPageChange: (page: number) => void
  onResend?: (invitation: InvitationInterface) => void
  onCancel?: (invitation: InvitationInterface) => void
  onReinvite?: (invitation: InvitationInterface) => void
}

export const InvitationsSection = ({
  communityPath,
  status,
  searchQuery,
  page,
  onPageChange,
  onResend,
  onCancel,
  onReinvite,
}: InvitationsSectionProps): ReactNode => {
  const { t } = useTranslation()
  const { data, isPending } = useInvitations({
    communityPath,
    status,
    page,
    searchBy: searchQuery,
  })

  if (isPending) {
    return (
      <div className="mb-4 grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] justify-center gap-5 px-5 sm:px-0 lg:justify-start lg:gap-3">
        {[...Array.from({ length: 9 }).keys()].map(index => (
          <SkeletonMemberShort key={index} />
        ))}
      </div>
    )
  }

  if (!data) {
    return null
  }

  const { data: invitations, total } = data

  if (total === 0) {
    return (
      <MembersEmptyList
        title={t('global.list.empty_title')}
        description={t(
          status === 'pending' ? 'members.empty.pending_section' : 'members.empty.expired_section',
        )}
        small
      />
    )
  }

  return (
    <>
      {invitations.length > 0 && (
        <div className="mb-4 grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] justify-center gap-5 px-5 sm:px-0 lg:justify-start lg:gap-3">
          {invitations.map(invitation => (
            <InvitationShort
              key={invitation.id}
              invitation={invitation}
              onResend={onResend}
              onCancel={onCancel}
              onReinvite={onReinvite}
            />
          ))}
        </div>
      )}
      {total > 0 && (
        <div className="flex justify-center rounded-lg bg-bg-primary p-3">
          <Pagination total={total} currentPage={page} onChangePage={onPageChange} pageSize={9} />
        </div>
      )}
    </>
  )
}
